import { useState } from "react";
import type { ReactNode } from "react";
import type { FileRejection } from "react-dropzone";
import { TransitionGroup } from "react-transition-group";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useSnapshotSection } from "@/components/snapshot/snapshotSection.context";
import { SnapshotDropzoneArea } from "./SnapshotDropzoneArea";
import { SnapshotDropzoneBuildButton } from "./SnapshotDropzoneBuildButton";
import { SnapshotDropzoneExistingState } from "./SnapshotDropzoneExistingState";
import { SnapshotDropzoneNotes } from "./SnapshotDropzoneNotes";
import { SnapshotDropzoneStatus } from "./SnapshotDropzoneStatus";
import { SnapshotDropzoneUploadedRow } from "./SnapshotDropzoneUploadedRow";
import { SnapshotDropzoneUploadingRow } from "./SnapshotDropzoneUploadingRow";
import { toRejectionText } from "./snapshotDropzone.utils";

interface SnapshotDropzoneProps {
  children?: ReactNode;
}

/**
 * One report's section body: a sticky, foldable header (name, status, file count, the children as
 * extra buttons, the build button), then its notes, the drop row and the day's files, newest first.
 * Reads everything from useSnapshotSection.
 */
export const SnapshotDropzone = (props: SnapshotDropzoneProps) => {
  const { children } = props;
  const section = useSnapshotSection();
  const [rejectionText, setRejectionText] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const sectionFiles = section.sectionFiles;
  const fileCount = sectionFiles.fileCount;
  const bodyId = `snapshot-section-${section.report}`;

  const handleDrop = (accepted: File[], rejections: FileRejection[]) => {
    setRejectionText(toRejectionText(rejections));
    if (accepted.length > 0) section.addFiles(accepted);
  };

  const handleToggle = () => {
    setIsOpen((current) => !current);
  };

  return (
    <Box component="section">
      <Stack
        direction="row"
        sx={{
          alignItems: "center",
          bgcolor: "background.default",
          borderTop: 1,
          borderColor: "divider",
          flexWrap: "wrap",
          gap: 1.5,
          pb: 1,
          position: "sticky",
          pt: 2,
          top: 0,
          zIndex: 2,
        }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            flex: "1 1 280px",
            flexWrap: "wrap",
            gap: 1.5,
            minWidth: 0,
          }}
        >
          <Typography variant="h2">
            <ButtonBase
              aria-controls={bodyId}
              aria-expanded={isOpen}
              onClick={handleToggle}
              sx={{
                borderRadius: 0.75,
                font: "inherit",
                gap: 1,
                "&:hover .SnapshotDropzone-chevron": { color: "text.primary" },
              }}
            >
              <ChevronRightIcon
                className="SnapshotDropzone-chevron"
                sx={{
                  color: "text.secondary",
                  fontSize: 18,
                  transform: isOpen ? "rotate(90deg)" : "none",
                  transition: "transform 160ms, color 160ms",
                }}
              />
              {section.label}
            </ButtonBase>
          </Typography>
          <SnapshotDropzoneStatus />
          <Typography
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
            variant="body2"
          >
            · {fileCount} {fileCount === 1 ? "file" : "files"}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center" }}
        >
          {children}
          <SnapshotDropzoneBuildButton />
        </Stack>
      </Stack>
      <Collapse
        id={bodyId}
        in={isOpen}
      >
        <Box sx={{ pt: 0.5 }}>
          <SnapshotDropzoneNotes />
          <Box sx={{ pb: 1.5 }}>
            <SnapshotDropzoneArea onDrop={handleDrop} />
            {rejectionText && (
              <Typography
                color="error"
                role="alert"
                sx={{
                  display: "block",
                  mt: 0.75,
                }}
                variant="caption"
              >
                {rejectionText}
              </Typography>
            )}
          </Box>
          <Box sx={{ mx: -1.5 }}>
            <TransitionGroup component={null}>
              {sectionFiles.droppedFiles.map((item) => (
                <Collapse key={item.id}>
                  <SnapshotDropzoneUploadingRow
                    item={item}
                    listed={item.key ? sectionFiles.fileByKey.get(item.key) : undefined}
                  />
                </Collapse>
              ))}
              {sectionFiles.otherFiles.map((file) => (
                <Collapse key={file.key}>
                  <SnapshotDropzoneUploadedRow file={file} />
                </Collapse>
              ))}
            </TransitionGroup>
          </Box>
          <SnapshotDropzoneExistingState />
        </Box>
      </Collapse>
    </Box>
  );
};
